import { UserProfileRole } from '@@types/api.types';
import type { CompanyTechnologyNestedSelectDto, TechnologySelectDto, UserSelectDto } from '@@types/api.types';
import { ExpertProfileViewModal } from '@components/pages/expert-profile/components/expert-profile-view-modal.component';
import { AvatarItem } from '@components/shared/avatar-item.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useGetMissionAssessmentRequest } from '@queries/mission-assessment-requests.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { ArrowRightIcon } from 'lucide-react';
import { FC, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaRequestDetailsCard } from './ma-request-details-card.component';

type MaContextColumnProps = {
  canDuplicate?: boolean;
  expert?: UserSelectDto;
  isDuplicating?: boolean;
  onDuplicate?: () => void;
  onNavigateToTab?: (tab: string) => void;
  requestId?: string;
  technology?: TechnologySelectDto;
  technologyId: string;
};

export const MaContextColumn: FC<MaContextColumnProps> = (props) => {
  const {
    canDuplicate,
    expert,
    isDuplicating,
    onDuplicate,
    onNavigateToTab,
    requestId,
    technology: technologyProp,
    technologyId,
  } = props;
  const [isExpertProfileOpen, setIsExpertProfileOpen] = useState(false);
  const navigate = useNavigate();

  const { data: currentUser } = useGetCurrentUser();
  const { data: request, isLoading: isRequestLoading } = useGetMissionAssessmentRequest(requestId);
  // Only fetch technology if not provided as prop
  const { data: fetchedTechnology, isLoading: isTechnologyLoading } = useGetTechnology(
    technologyProp ? undefined : technologyId,
  );

  const technology = technologyProp ?? fetchedTechnology;

  // Only show loading if we're actually fetching (no prop provided and loading)
  const isLoading = (!technologyProp && isTechnologyLoading) || isRequestLoading;
  const userRole = currentUser?.profile?.role;
  const canOpenOrganization =
    userRole === UserProfileRole.INNOVATOR ||
    ((userRole === UserProfileRole.EXPERT || userRole === UserProfileRole.INTELLIGENCE) && Boolean(onNavigateToTab));

  const handleOpenExpertProfile = (): void => {
    setIsExpertProfileOpen(true);
  };

  const handleCloseExpertProfile = (): void => {
    setIsExpertProfileOpen(false);
  };

  const handleOpenOrganization = (): void => {
    if (userRole === UserProfileRole.INNOVATOR) {
      navigate('/organization');
      return;
    }

    if (userRole === UserProfileRole.EXPERT || userRole === UserProfileRole.INTELLIGENCE) {
      onNavigateToTab?.('organization');
    }
  };

  if (isLoading) {
    return <ContextColumnLoadingState />;
  }

  return (
    <>
      <aside className="w-full flex flex-col gap-3">
        {canDuplicate && onDuplicate && (
          <Tile className="border border-gray-100 p-4 shadow-sm">
            <Button
              className="w-full justify-center"
              loading={isDuplicating}
              onClick={onDuplicate}
              variant="primary"
            >
              Duplicate Assessment
            </Button>
          </Tile>
        )}

        {/* Organization Summary */}
        {technology?.company && (
          <ContextSidebarTile
            onActionClick={canOpenOrganization ? handleOpenOrganization : undefined}
            title="Organization"
          >
            <OrganizationContent company={technology.company} />
          </ContextSidebarTile>
        )}

        {/* Expert Details */}
        {expert && (
          <ContextSidebarTile
            onActionClick={handleOpenExpertProfile}
            title="Expert Details"
          >
            <ExpertDetailsContent expert={expert} />
          </ContextSidebarTile>
        )}

        {/* Tech Use & Domain */}
        {technology && (
          <ContextSidebarTile title="Technology Use & Domain">
            <TechUseDomainContent technology={technology} />
          </ContextSidebarTile>
        )}

        {/* Assessment Request - only show if request exists */}
        {request && (
          <ContextSidebarTile
            hasContentPadding={false}
            title="Request Details"
          >
            <MaRequestDetailsCard request={request} />
          </ContextSidebarTile>
        )}
      </aside>

      {expert && (
        <ExpertProfileViewModal
          isOpen={isExpertProfileOpen}
          onClose={handleCloseExpertProfile}
          user={expert}
        />
      )}
    </>
  );
};

type ContextSidebarTileProps = {
  children: ReactNode;
  hasContentPadding?: boolean;
  onActionClick?: () => void;
  title: string;
};

const ContextSidebarTile: FC<ContextSidebarTileProps> = (props) => {
  const { children, hasContentPadding = true, onActionClick, title } = props;

  return (
    <Tile
      className="p-0 border border-gray-100 shadow-sm overflow-hidden"
      contentClassName="gap-0"
      header={{
        className: 'px-4 py-3',
        iconButton: onActionClick
          ? {
              icon: <ArrowRightIcon size={16} />,
              onClick: onActionClick,
            }
          : undefined,
        title,
      }}
      hoverReveal={Boolean(onActionClick)}
    >
      <div className={hasContentPadding ? 'px-4 pb-4' : undefined}>{children}</div>
    </Tile>
  );
};

const ContextColumnLoadingState: FC = () => {
  return (
    <aside className="w-full flex flex-col gap-3">
      <Tile
        className="min-h-[100px]"
        isLoading
      />
      <Tile
        className="min-h-[80px]"
        isLoading
      />
      <Tile
        className="min-h-[120px]"
        isLoading
      />
    </aside>
  );
};

type OrganizationContentProps = {
  company: CompanyTechnologyNestedSelectDto;
};

const OrganizationContent: FC<OrganizationContentProps> = (props) => {
  const { company } = props;

  const locationParts = [company.city, company.state, company.country].filter(Boolean);
  const locationDisplay = locationParts.join(', ');
  const websiteUrl = company.urls?.website;
  const websiteLabel = websiteUrl?.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <div className="flex flex-col gap-3 text-sm text-gray-600">
      <AvatarItem
        altText={`${company.name} Logo`}
        className="items-center"
        imageUrl={company.logoUrl}
        initialText={company.name}
        subtitle={locationDisplay}
        size="md"
        title={<span className="font-medium text-gray-800">{company.name}</span>}
      />

      {websiteLabel && (
        <div className="flex flex-col gap-0.5">
          <span className="truncate font-medium text-gray-700">{websiteLabel}</span>
          <BlueLabel text="Website" />
        </div>
      )}
    </div>
  );
};

type TechUseDomainContentProps = {
  technology: TechnologySelectDto;
};

const TechUseDomainContent: FC<TechUseDomainContentProps> = (props) => {
  const { technology } = props;

  // Get primary use (first by position)
  const sortedUses = technology.uses ? [...technology.uses].sort((a, b) => a.position - b.position) : [];
  const primaryUse = sortedUses.length > 0 ? sortedUses[0] : null;

  // Get primary domain from primary use (first domain by position)
  const sortedDomains = primaryUse?.domains ? [...primaryUse.domains].sort((a, b) => a.position - b.position) : [];
  const primaryDomain = sortedDomains.length > 0 ? sortedDomains[0] : null;

  if (!primaryUse && !primaryDomain) {
    return <span className="text-sm text-gray-500">No use or domain configured</span>;
  }

  return (
    <div className="flex flex-col gap-3 text-sm text-gray-600">
      {primaryUse && (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-gray-700">{primaryUse.name}</span>
          <BlueLabel text="Primary Use" />
        </div>
      )}

      {primaryDomain && (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-gray-700">{primaryDomain.name}</span>
          <BlueLabel text="Primary Domain" />
        </div>
      )}
    </div>
  );
};

type ExpertDetailsContentProps = {
  expert: UserSelectDto;
};

const getExpertDisplayInfo = (expert: UserSelectDto): { name: string; title?: string } => {
  const firstName = expert.profile?.firstName;
  const fullName = expert.profile?.fullName;
  const lastName = expert.profile?.lastName;

  return {
    name: fullName ?? (firstName ? `${firstName}${lastName ? ` ${lastName}` : ''}` : 'Expert'),
    title: expert.profile?.title,
  };
};

const ExpertDetailsContent: FC<ExpertDetailsContentProps> = (props) => {
  const { expert } = props;
  const { name, title } = getExpertDisplayInfo(expert);

  return (
    <div className="flex flex-col gap-1">
      <AvatarItem
        altText={name}
        imageUrl={expert.avatarUrl ?? undefined}
        initialText={name}
        size="md"
        subtitle={title}
        title={<span className="font-medium text-gray-900">{name}</span>}
      />
    </div>
  );
};
